import apiFetch from "@/app/auth/apiFetch";

const API_URL = import.meta.env.VITE_API_URL;

export interface CandidateListItem {
  id: number;
  profile_url: string;
  first_name: string;
  last_name: string;
  title?: string | null;
  image_url?: string | null;
  location?: string | null;
  created_at: string;
  is_processed?: boolean;
  processed_at?: string | null;
  total_seniority?: number | null;
  average_job_duration?: number | null;
  max_gap_between_jobs?: number | null;
}

export interface PaginatedCandidatesResponse {
  items: CandidateListItem[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export async function listCandidates(
  navigate: NavigateFunction,
  params?: { page?: number; size?: number; sort_by?: string; sort_order?: string }
) {
  const q = new URLSearchParams();
  q.set("page", String(params?.page ?? 1));
  q.set("size", String(params?.size ?? 25));
  if (params?.sort_by) q.set("sort_by", params.sort_by);
  if (params?.sort_order) q.set("sort_order", params.sort_order);
  const url = `${API_URL}/candidates?${q.toString()}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des candidats");
  return res.json() as Promise<PaginatedCandidatesResponse>;
}

export interface ExperienceItem {
  id: number;
  job_title: string;
  contract_type?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  location?: string | null;
  description?: string | null;
  company_name?: string | null;
  company_id?: number | null;
}

export interface EducationItem {
  id: number;
  degree?: string | null;
  field_of_study?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  school_name?: string | null;
  school_id?: number | null;
}

export interface LanguageItem {
  id: number;
  name: string;
  proficiency?: string | null;
}

export interface CertificationItem {
  id: number;
  name: string;
  authority?: string | null;
  license_number?: string | null;
  url?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

export interface InterestItem {
  id: number;
  name: string;
  interest_type?: string | null;
  url?: string | null;
  follower_count?: number | null;
}

export interface VerificationItem {
  company_name: string;
  verification_type: "work_email" | "recruiter_license" | string;
  verified_month: number;
  verified_year: number;
}

export interface CandidateDetail extends CandidateListItem {
  profile_urn?: string | null;
  headline?: string | null;
  summary?: string | null;
  email?: string | null;
  phone?: string | null;
  experiences: ExperienceItem[];
  educations: EducationItem[];
  skills?: string[];
  languages?: LanguageItem[];
  certifications?: CertificationItem[];
  interests?: InterestItem[];
  linkedin_member_since_month?: number | null;
  linkedin_member_since_year?: number | null;
  linkedin_contact_info_fresh?: string | null;
  linkedin_photo_fresh?: string | null;
  about_this_profile_synced_at?: string | null;
  verifications?: VerificationItem[];
}

export async function getCandidateDetail(navigate, candidateId: number) {
  const url = `${API_URL}/candidates/${candidateId}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement du candidat");
  return res.json() as Promise<CandidateDetail>;
}

export async function enqueueCandidateEnrichment(navigate, candidateId: number) {
  const url = `${API_URL}/candidates/${candidateId}/enqueue-enrichment`;
  const res = await apiFetch(url, { method: "POST" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la mise en file d'enrichissement");
  return res.json() as Promise<{ status: string; candidate_id: number }>;
}
